/* ============================================================
   Driftroot theme JS
   - Scroll reveal
   - Mobile menu
   - FAQ accordion
   - AJAX add-to-cart via Shopify /cart/add.js
   - Cart drawer (open, render, update qty, remove line)
   - Sticky bar show/hide
   - Nav shrink on scroll
   - Video autoplay fallback
   ============================================================ */

(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const money = (cents) => {
    // Simple USD formatter. Shopify's money filter handles currency server-side;
    // this is for client-rendered line items only.
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  };

  // ===== SCROLL REVEAL =====
  const revealAll = () => $$('[data-reveal]').forEach(el => el.classList.add('shown'));
  if ('IntersectionObserver' in window) {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('shown');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.02, rootMargin: '0px 0px 150px 0px' });
    $$('[data-reveal]').forEach(el => obs.observe(el));
  } else {
    revealAll();
  }
  setTimeout(revealAll, 2500);
  window.addEventListener('scroll', revealAll, { once: true });

  // ===== FAQ ACCORDION =====
  $$('.faq-q').forEach(btn => {
    btn.addEventListener('click', () => {
      const answer = btn.nextElementSibling;
      const isOpen = btn.classList.contains('active');
      $$('.faq-q').forEach(q => {
        q.classList.remove('active');
        q.setAttribute('aria-expanded', 'false');
        if (q.nextElementSibling) q.nextElementSibling.style.maxHeight = null;
      });
      if (!isOpen) {
        btn.classList.add('active');
        btn.setAttribute('aria-expanded', 'true');
        answer.style.maxHeight = answer.scrollHeight + 'px';
      }
    });
  });

  // ===== MOBILE MENU =====
  const menuBtn = $('#menuToggle');
  if (menuBtn) {
    menuBtn.addEventListener('click', () => {
      const links = $('.nav-links');
      if (!links) return;
      const isOpen = links.style.display === 'flex';
      Object.assign(links.style, {
        display: isOpen ? 'none' : 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top: '100%',
        left: '0',
        right: '0',
        background: 'rgba(250,250,245,.98)',
        padding: '24px',
        borderBottom: '1px solid rgba(45,107,74,.14)',
        gap: '16px',
        zIndex: '999',
      });
    });
  }

  // ===== NAV SHRINK =====
  const nav = $('.nav');
  if (nav) {
    window.addEventListener('scroll', () => {
      nav.style.padding = window.scrollY > 80 ? '10px 0' : '16px 0';
    }, { passive: true });
  }

  // ===== STICKY BAR =====
  const stickyBar = $('#stickyBar');
  const productsSection = $('#products');
  if (stickyBar && productsSection) {
    window.addEventListener('scroll', () => {
      const past = window.scrollY > (productsSection.offsetTop + productsSection.offsetHeight);
      stickyBar.classList.toggle('show', past);
    }, { passive: true });
  }

  // ===== BACKGROUND VIDEO FALLBACK =====
  const bgVid = $('#bgVideo');
  if (bgVid) {
    bgVid.play().catch(() => {});
    ['click', 'touchstart', 'scroll'].forEach(evt => {
      document.addEventListener(evt, () => { bgVid.play().catch(() => {}); }, { once: true });
    });
  }

  // ============================================================
   // CART
   // ============================================================

  const drawer = $('#cartDrawer');
  const drawerBody = $('#cartDrawerBody');
  const drawerFoot = $('#cartDrawerFoot');
  const subtotalEl = $('#cartSubtotal');
  const cartCountEls = () => $$('[data-cart-count]');

  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  $$('[data-cart-open]').forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); renderCart(); }));
  $$('[data-cart-close]').forEach(el => el.addEventListener('click', closeDrawer));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function addToCart(variantId, quantity = 1, sellingPlan = null) {
    const body = { items: [{ id: Number(variantId), quantity }] };
    if (sellingPlan) body.items[0].selling_plan = Number(sellingPlan);
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Add to cart failed');
    }
    return res.json();
  }

  async function changeLine(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  function renderCartHTML(cart) {
    if (!cart || cart.item_count === 0) {
      drawerBody.innerHTML = '<p class="cart-drawer-empty">Your cart is empty.</p>';
      if (drawerFoot) drawerFoot.hidden = true;
      return;
    }
    const lines = cart.items.map(item => {
      const planName = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : '';
      const imgSrc = item.image ? item.image : '';
      return `
        <div class="cart-line" data-line-key="${item.key}">
          ${imgSrc ? `<img src="${imgSrc}" alt="${item.product_title}" loading="lazy">` : ''}
          <div class="cart-line-body">
            <div class="cart-line-title">${item.product_title}</div>
            ${planName ? `<div class="cart-line-variant">${planName}</div>` : ''}
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-line-variant">${item.variant_title}</div>` : ''}
            <div class="cart-line-bottom">
              <div class="cart-qty" role="group" aria-label="Quantity">
                <button type="button" data-qty-dec aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-qty-inc aria-label="Increase quantity">+</button>
              </div>
              <div class="cart-line-price">${money(item.final_line_price)}</div>
            </div>
            <button type="button" class="cart-line-remove" data-remove>Remove</button>
          </div>
        </div>
      `;
    }).join('');
    drawerBody.innerHTML = lines;
    if (drawerFoot) {
      drawerFoot.hidden = false;
      if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    }
    wireLineControls(cart);
  }

  function wireLineControls(cart) {
    $$('.cart-line').forEach(line => {
      const key = line.getAttribute('data-line-key');
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      line.querySelector('[data-qty-inc]')?.addEventListener('click', async () => {
        const c = await changeLine(key, item.quantity + 1);
        renderCartHTML(c);
        updateCartCount(c.item_count);
      });
      line.querySelector('[data-qty-dec]')?.addEventListener('click', async () => {
        const c = await changeLine(key, Math.max(0, item.quantity - 1));
        renderCartHTML(c);
        updateCartCount(c.item_count);
      });
      line.querySelector('[data-remove]')?.addEventListener('click', async () => {
        const c = await changeLine(key, 0);
        renderCartHTML(c);
        updateCartCount(c.item_count);
      });
    });
  }

  function updateCartCount(n) {
    cartCountEls().forEach(el => { el.textContent = n; });
  }

  async function renderCart() {
    try {
      const cart = await fetchCart();
      renderCartHTML(cart);
      updateCartCount(cart.item_count);
    } catch (err) {
      console.error(err);
    }
  }

  // ===== INTERCEPT ALL ADD-TO-CART FORMS =====
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action="/cart/add"], form[data-atc-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('button[type="submit"], .atc, .sub-cta, .pdp-atc');
    const originalText = btn ? btn.querySelector('.btn-text')?.textContent || btn.textContent : '';

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
    const sellingPlan = formData.get('selling_plan');

    if (!variantId) return;

    if (btn) {
      btn.disabled = true;
      if (btn.querySelector('.btn-text')) btn.querySelector('.btn-text').textContent = 'Adding...';
      else btn.textContent = 'Adding...';
    }

    try {
      await addToCart(variantId, quantity, sellingPlan || null);
      if (btn) {
        btn.classList.add('added');
        if (btn.querySelector('.btn-text')) btn.querySelector('.btn-text').textContent = 'Added!';
        else btn.textContent = 'Added!';
      }
      await renderCart();
      openDrawer();
      setTimeout(() => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('added');
          if (btn.querySelector('.btn-text')) btn.querySelector('.btn-text').textContent = originalText;
          else btn.textContent = originalText;
        }
      }, 1800);
    } catch (err) {
      console.error('Add to cart error:', err);
      alert(err.message || 'Could not add to cart.');
      if (btn) {
        btn.disabled = false;
        if (btn.querySelector('.btn-text')) btn.querySelector('.btn-text').textContent = originalText;
        else btn.textContent = originalText;
      }
    }
  });

  // ===== PDP VARIANT / SELLING PLAN SYNC =====
  // When user toggles one-time vs subscribe radio on the PDP, update the hidden selling_plan input.
  $$('.purchase-options').forEach(group => {
    group.addEventListener('change', (e) => {
      const form = group.closest('form');
      if (!form) return;
      const val = e.target.value;
      let hidden = form.querySelector('input[name="selling_plan"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'selling_plan';
        form.appendChild(hidden);
      }
      hidden.value = val || '';
    });
  });

  // ===== INITIAL CART COUNT =====
  fetchCart().then(c => updateCartCount(c.item_count)).catch(() => {});
})();

/* ============================================================
   v8 conversion features
   ============================================================ */

(() => {
  'use strict';
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  // ===== REAL COUNTDOWN =====
  const countdownEl = $('[data-countdown]');
  const countdownTarget = $('[data-countdown-target]');
  if (countdownEl && countdownTarget) {
    const target = Number(countdownTarget.getAttribute('data-countdown-target')) * 1000;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const bar = countdownEl.closest('.urgency');
        if (bar) bar.style.display = 'none';
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff / 3600000) % 24);
      const m = Math.floor((diff / 60000) % 60);
      const s = Math.floor((diff / 1000) % 60);
      const pad = n => String(n).padStart(2, '0');
      countdownEl.textContent = d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
      requestAnimationFrame(() => setTimeout(tick, 1000));
    };
    tick();
  }

  // ===== FREE SHIPPING PROGRESS BAR =====
  const shippingBar = $('.cart-shipping-bar');
  const shippingMsg = $('[data-shipping-message]');
  const shippingFill = $('[data-shipping-fill]');
  const threshold = shippingBar ? Number(shippingBar.getAttribute('data-shipping-threshold')) : 0;
  const formatMoney = cents => '$' + (cents / 100).toFixed(2);

  function updateShippingBar(totalCents) {
    if (!shippingBar || threshold <= 0) return;
    if (totalCents >= threshold) {
      shippingMsg.innerHTML = '🎉 <strong>You qualified for free shipping!</strong>';
      shippingMsg.classList.add('qualified');
      shippingFill.style.width = '100%';
    } else {
      const remaining = threshold - totalCents;
      shippingMsg.innerHTML = `Add <strong>${formatMoney(remaining)}</strong> to unlock free shipping`;
      shippingMsg.classList.remove('qualified');
      const pct = totalCents > 0 ? Math.max(4, (totalCents / threshold) * 100) : 0;
      shippingFill.style.width = pct + '%';
    }
  }

  // Hook into existing cart render: observe cart subtotal changes
  const subtotalEl = $('#cartSubtotal');
  if (subtotalEl) {
    const observer = new MutationObserver(() => {
      // Re-fetch cart to get total_price in cents (subtotal text is formatted)
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => updateShippingBar(cart.total_price))
        .catch(() => {});
    });
    observer.observe(subtotalEl, { childList: true, characterData: true, subtree: true });
  }
  // Initial load
  fetch('/cart.js', { credentials: 'same-origin' })
    .then(r => r.json())
    .then(cart => {
      updateShippingBar(cart.total_price);
      // Show upsell only when there's at least 1 item
      const upsell = $('#cartUpsell');
      if (upsell && cart.item_count > 0) {
        // Hide if upsell product is already in cart
        const upsellVariantId = Number(upsell.getAttribute('data-upsell-product-id'));
        const alreadyInCart = cart.items.some(i => Number(i.variant_id) === upsellVariantId);
        upsell.hidden = alreadyInCart;
      }
    })
    .catch(() => {});

  // ===== CART UPSELL ADD BUTTON =====
  const upsellAddBtn = $('[data-upsell-add]');
  if (upsellAddBtn) {
    upsellAddBtn.addEventListener('click', async () => {
      const upsell = $('#cartUpsell');
      const variantId = Number(upsell.getAttribute('data-upsell-product-id'));
      upsellAddBtn.disabled = true;
      upsellAddBtn.textContent = 'Adding...';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        if (!res.ok) throw new Error('add failed');
        upsellAddBtn.classList.add('added');
        upsellAddBtn.textContent = 'Added ✓';
        // Trigger cart re-render
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        // Simulate the existing renderCart call from the main JS by firing a visibilitychange
        window.dispatchEvent(new Event('cart:updated'));
        upsell.hidden = true;
        // Nudge: open cart if closed
        const drawer = $('#cartDrawer');
        if (drawer && !drawer.classList.contains('open')) drawer.classList.add('open');
      } catch (err) {
        upsellAddBtn.disabled = false;
        upsellAddBtn.textContent = 'Add +';
        console.error(err);
      }
    });
  }

  // When cart:updated fires, refresh drawer manually
  window.addEventListener('cart:updated', () => {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        updateShippingBar(cart.total_price);
        // Update count badges
        $$('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
      });
  });

  // ===== EXIT-INTENT POPUP =====
  const exitPopup = $('#exitPopup');
  const exitForm = $('#exitPopupForm');
  if (exitPopup && !sessionStorage.getItem('driftroot_exit_shown')) {
    let armed = false;
    setTimeout(() => { armed = true; }, 8000); // arm after 8s on page

    const showExit = () => {
      if (!armed || sessionStorage.getItem('driftroot_exit_shown')) return;
      sessionStorage.setItem('driftroot_exit_shown', '1');
      exitPopup.hidden = false;
      // Force reflow so transition fires
      requestAnimationFrame(() => exitPopup.classList.add('show'));
    };

    document.addEventListener('mouseout', (e) => {
      if (e.clientY < 5 && !e.relatedTarget) showExit();
    });
    // Mobile: show after 30s if not yet shown
    setTimeout(() => { if (armed && !sessionStorage.getItem('driftroot_exit_shown')) showExit(); }, 30000);
  }

  const exitClose = () => {
    if (!exitPopup) return;
    exitPopup.classList.remove('show');
    setTimeout(() => { exitPopup.hidden = true; }, 350);
  };
  $$('[data-exit-close]').forEach(el => el.addEventListener('click', exitClose));
  if (exitPopup) {
    exitPopup.addEventListener('click', (e) => { if (e.target === exitPopup) exitClose(); });
  }
  if (exitForm) {
    exitForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = exitForm.querySelector('input[type="email"]').value.trim();
      if (!email) return;
      // Success state — local only; hook up to email provider later
      const popup = exitForm.closest('.exit-popup');
      popup.classList.add('success');
      popup.innerHTML = `
        <button type="button" class="exit-popup-close" data-exit-close aria-label="Close">×</button>
        <div class="exit-popup-icon">✓</div>
        <h3>Check your email</h3>
        <p>Your 10% off code is on its way to <strong>${email.replace(/[<>]/g,'')}</strong>. Use it at checkout.</p>
        <p class="exit-popup-note">If you don't see it in 5 minutes, check your spam folder.</p>
      `;
      popup.querySelector('[data-exit-close]').addEventListener('click', exitClose);
    });
  }
})();

/* ============================================================
   v9 — Product Quiz interaction
   ============================================================ */

(() => {
  const quiz = document.querySelector('[data-quiz]');
  if (!quiz) return;

  const steps = quiz.querySelectorAll('[data-quiz-step]');
  const progressFill = quiz.querySelector('[data-quiz-progress]');
  const totalSteps = 4;
  let currentStep = 1;
  const answers = [];

  // Parse recommendation data
  let recs = [];
  try {
    const dataEl = quiz.querySelector('[data-quiz-data]');
    if (dataEl) recs = JSON.parse(dataEl.textContent.trim() || '[]');
  } catch (e) { console.error('Quiz data parse:', e); }

  function showStep(stepId) {
    steps.forEach(s => s.removeAttribute('data-active'));
    const el = quiz.querySelector(`[data-quiz-step="${stepId}"]`);
    if (el) el.setAttribute('data-active', 'true');
    if (typeof stepId === 'number' && progressFill) {
      progressFill.style.width = (stepId / totalSteps * 100) + '%';
    } else if (progressFill) {
      progressFill.style.width = '100%';
    }
  }

  function pickRecommendation() {
    // Primary goal is answer[0]; find recs matching that goal
    const goal = answers[0] || 'sleep';
    const matches = recs.filter(r => (r.goals || '').split(',').map(s => s.trim()).includes(goal));
    return matches[0] || recs[0] || null;
  }

  function showResult() {
    showStep('result');
    const card = quiz.querySelector('[data-quiz-result-card]');
    const title = quiz.querySelector('[data-quiz-result-title]');
    const rec = pickRecommendation();
    if (!rec) {
      card.innerHTML = `<div class="quiz-result-info"><p style="color:var(--text-m)">We couldn't find a match. Browse <a href="/collections/all" style="color:var(--gold)">all products</a>.</p></div>`;
      return;
    }
    if (title) title.textContent = `Your match: ${rec.title}`;
    const subtitle = rec.subtitle || 'Recommended based on your answers';
    card.innerHTML = `
      ${rec.image ? `<img src="${rec.image}" alt="${rec.title}" loading="lazy">` : ''}
      <div class="quiz-result-info">
        <div class="quiz-result-title">${rec.title}</div>
        <div class="quiz-result-subtitle">${subtitle}</div>
        <div class="quiz-result-price">${rec.price}</div>
        <div class="quiz-result-actions">
          <form method="post" action="/cart/add" data-atc-form style="display:inline">
            <input type="hidden" name="id" value="${rec.variant_id}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="btn btn-gold"><span class="btn-text">Add to cart</span></button>
          </form>
          <a href="${rec.url}" class="btn btn-ghost">View details</a>
        </div>
      </div>
    `;
  }

  quiz.addEventListener('click', (e) => {
    const opt = e.target.closest('[data-quiz-answer]');
    if (opt) {
      const answer = opt.getAttribute('data-quiz-answer');
      answers[currentStep - 1] = answer;
      if (currentStep < totalSteps) {
        currentStep++;
        showStep(currentStep);
      } else {
        showResult();
      }
      return;
    }
    if (e.target.closest('[data-quiz-restart]')) {
      currentStep = 1;
      answers.length = 0;
      showStep(1);
    }
  });
})();
